import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import List from '@embracesbs/component-list/List'
import Button2 from '@embracesbs/component-button2/Button2'
import { Subscription, Tenant } from '../../Interfaces/Types'
import Icon from '@embracesbs/component-icon'
import Text from '@embracesbs/component-text'
import { itemColor } from '../../Utilities/helpers/helpers'
import { useSubscriptions } from '../Context/SubscriptionContext'
import { useMediaQuery } from '../../Hooks'
import TenantList from '../Tenants/TenantList'
import TenantListItem from '../Tenants/TenantListItem'
import AddCard from '../Common/AddCard'
import { AddSubscription } from '../Section1/Modals'

interface SubscriptionListProps {
  query: string
  searchResults: { subscriptions?: Subscription[]; tenants?: Tenant[] }
  isSearchLoading: boolean
  showSubscriptionsWithTenant: boolean
  showSubscriptionsWithoutTenant: boolean
}

const nestedStyle: CSSProperties = { paddingLeft: '16px', marginBottom: '6px' }

const spinner = (
  <Icon
    color={itemColor(undefined, false)}
    iconName='EmailActionSync1'
    size={16}
    style={{ width: '100%', margin: '10px 0', animation: 'spin 1s linear infinite' }}
  />
)

const SubscriptionList: React.FC<SubscriptionListProps> = ({
  query,
  searchResults,
  isSearchLoading,
  showSubscriptionsWithTenant,
  showSubscriptionsWithoutTenant
}) => {
  const { subscriptions, selectedSubscriptionId, setSelectedSubscriptionId, addSubscription, loading } = useSubscriptions()
  const [addSubModalOpen, setAddSubModalOpen] = useState(false)
  // On narrow screens the Select/Close buttons shrink to icon-only.
  const isCompact = useMediaQuery('(max-width: 1400px)')
  const isSearching = !!query
  const selectedRef = useRef<HTMLDivElement | null>(null)

  // When a subscription becomes selected (e.g. picked from search, which then
  // switches back to this accordion), scroll its card into view — otherwise the
  // selected item is often below the fold in a long list.
  // `block: 'nearest'` keeps the scroll inside the list's own scroll area; 'start'
  // could bubble up and scroll the whole page off-screen (with no way back).
  useEffect(() => {
    if (!isSearching && selectedSubscriptionId && selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedSubscriptionId, isSearching])

  // Inline "Add subscription" card shown at the top of the normal list (never in search).
  const addSubscriptionBlock = (
    <>
      <AddCard label='Add subscription' onClick={() => setAddSubModalOpen(true)} />
      {addSubModalOpen && (
        <AddSubscription onCreated={(data) => addSubscription(data)} onClosed={() => setAddSubModalOpen(false)} isOpen={addSubModalOpen} />
      )}
    </>
  )

  // Normal (no search): filter by the with/without-tenants toggle.
  const filteredSubscriptions = useMemo(() => {
    if (showSubscriptionsWithTenant) {
      return subscriptions.filter((s: { hasTenants: boolean }) => s.hasTenants)
    }
    if (showSubscriptionsWithoutTenant) {
      return subscriptions.filter((s: { hasTenants: boolean }) => !s.hasTenants)
    }
    return subscriptions
  }, [subscriptions, showSubscriptionsWithTenant, showSubscriptionsWithoutTenant])

  // Search: subscriptions that match by name, plus the parents of any matched
  // tenant — keeping the hierarchy instead of a flat results list.
  const searchSubscriptions = useMemo(() => {
    if (!isSearching) return [] as Subscription[]
    const relevantIds = new Set<string>([
      ...(searchResults.subscriptions ?? []).map((s) => s.id),
      ...(searchResults.tenants ?? []).map((t) => t.subscriptionId)
    ])
    return (subscriptions as Subscription[]).filter((s) => relevantIds.has(s.id))
  }, [isSearching, searchResults, subscriptions])

  const renderSubCard = (item: Subscription, isSelected: boolean) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        // Per-side longhands only: mixing the `borderColor` shorthand with
        // `borderLeftColor` makes React updates overwrite the left accent.
        borderStyle: 'solid',
        borderTopWidth: '1px',
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '4px',
        borderTopColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
        borderRightColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
        borderBottomColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
        borderLeftColor: 'var(--tma-accent)',
        borderRadius: '6px',
        background: 'var(--tma-panel-bg)',
        padding: '12px 14px',
        marginBottom: '6px',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s ease'
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <Text textStyle='body' content={item.customerName} singleLine hasTooltipEllipsis />
      </div>
      <Button2
        style={{ flexShrink: 0, minWidth: isCompact ? undefined : '92px', borderRadius: '8px', justifyContent: 'center' }}
        buttonSize='30px'
        leftElement={<Icon color={isSelected ? '#ffffff' : '#416eec'} iconName={isSelected ? 'ArrowUp1' : 'ArrowRight1'} size={12} />}
        tabIndex={-1}
        onClick={() => setSelectedSubscriptionId(isSelected ? null : item.id)}
        text={isCompact ? '' : isSelected ? 'Close' : 'Select'}
        aria-label={isSelected ? 'Close' : 'Select'}
        type='button'
        variant={isSelected ? 'contained' : 'outlined'}
      />
    </div>
  )

  if (loading) {
    return spinner
  }

  // --- Search mode: filtered hierarchy ---
  if (isSearching) {
    if (isSearchLoading) return spinner
    const hasMatches = searchSubscriptions.length > 0 || (searchResults.tenants ?? []).length > 0
    if (!hasMatches) {
      return (
        <Text align='center' textColor='grey'>
          No matches found
        </Text>
      )
    }
    return (
      <List as='div' role='list' ariaLabel='Search results'>
        {searchSubscriptions.map((item: Subscription) => {
          const matchingTenants = (searchResults.tenants ?? []).filter((t) => t.subscriptionId === item.id)
          return (
            <React.Fragment key={item.id}>
              {renderSubCard(item, false)}
              {matchingTenants.length > 0 && (
                <div style={nestedStyle}>
                  {matchingTenants.map((tenant: Tenant) => (
                    <TenantListItem key={tenant.id} tenant={tenant} />
                  ))}
                </div>
              )}
            </React.Fragment>
          )
        })}
      </List>
    )
  }

  // --- Normal mode: accordion ---
  if (filteredSubscriptions?.length === 0) {
    return (
      <>
        {addSubscriptionBlock}
        <Text align='center' textColor='grey'>
          No subscriptions found
        </Text>
      </>
    )
  }

  return (
    <>
      {addSubscriptionBlock}
      <List as='div' role='list' ariaLabel='Subscription List'>
        {filteredSubscriptions?.map((item: Subscription) => {
          const isSelected = item.id === selectedSubscriptionId
          return (
            <div key={item.id} ref={isSelected ? selectedRef : undefined} style={{ scrollMarginTop: '8px' }}>
              {renderSubCard(item, isSelected)}
              {isSelected && (
                <div style={nestedStyle}>
                  <TenantList query={query} />
                </div>
              )}
            </div>
          )
        })}
      </List>
    </>
  )
}

export default SubscriptionList
